import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotesPage from '../pages/NotesPage';
import ManagePages from '../pages/ManagePages';
import MainPage from './MainPage';
import OpenPage from '../pages/OpenPage';
import BottomBar from './BottomBar';
import SettingsMenu from './SettingsMenu';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
// 懶加載 API 監控 Dialog
const ApiMonitorDialog = lazy(() => import('./ApiMonitorDialog'));
import { TimecodePosition, StepUnit, ScreenshotSize } from '../types';
import { 
  initializeUserSettings, 
  updateUserSetting,
  DEFAULT_USER_SETTINGS 
} from '../services/userSettingsService';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PublicIcon from '@mui/icons-material/Public';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import QrCodeIcon from '@mui/icons-material/QrCode';
import GroupIcon from '@mui/icons-material/Group';
import HistoryIcon from '@mui/icons-material/History';
import NoteIcon from '@mui/icons-material/Note';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import FolderIcon from '@mui/icons-material/Folder';

// 定義頁面類型
type PageType = 'open' | 'notes' | 'main' | 'manage';

// 定義頁面狀態接口
interface PageState {
  open: {
    scrollPosition: number;
    youtubeDialogOpen: boolean;
    youtubeUrl: string;
    authDialogOpen: boolean;
    pendingAction: string | null;
  };
  notes: {
    scrollPosition: number;
    searchKeyword: string;
    selectedMonths: string[];
    isSearching: boolean;
    filteredNotes: any[];
  };
  manage: {
    currentSection: string;
    expandedMain: string[];
    scrollPosition: number;
    selectedTab: number;
  };
  main: {
    videoId: string | null;
    isPlaying: boolean;
    currentTime: number;
    playbackRate: number;
  };
}

// 定義導航狀態接口
interface NavigationState {
  shouldNavigateToMain: boolean;
  navigationData: any;
  source: string;
}

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser, isAuthenticated } = useAuth();
  
  // 🚀 優化：監聽登入成功後的自動導航
  useEffect(() => {
    const handleNavigateToNotes = () => {
      console.log('🚀 收到自動導航到 NotesPage 事件');
      setCurrentPage('notes');
    };
    
    window.addEventListener('navigateToNotes', handleNavigateToNotes);
    
    return () => {
      window.removeEventListener('navigateToNotes', handleNavigateToNotes);
    };
  }, []);
  
  // 🔧 修復：移除自動跳轉到 NotesPage，讓用戶登入後停留在 OpenPage
  // useEffect(() => {
  //   if (authUser && isAuthenticated) {
  //     console.log('🚀 用戶已登入，準備自動導航到 NotesPage...');
  //     // 立即導航，因為數據已經預載入
  //     const timer = setTimeout(() => {
  //       console.log('🚀 自動導航到 NotesPage');
  //       setCurrentPage('notes');
  //     }, 200); // 0.2秒後導航，更快響應
  //     
  //     return () => clearTimeout(timer);
  //   }
  // }, [authUser, isAuthenticated]);
  
  // 🔧 調試：確認 SPA 模式狀態（只記錄一次）
  useEffect(() => {
    console.log('🚀 MainLayout 已啟動 - SPA 模式');
    console.log('📍 當前路徑:', location.pathname);
    console.log('🌐 當前 URL:', window.location.href);
  }, []); // 只在組件掛載時記錄一次

  // 🔧 修復：處理 BottomBar 導航（暫時移除依賴，稍後重新定義）
  const handleBottomBarNavigation = useCallback((page: PageType) => {
    console.log(`🔄 切換到頁面: ${page}`);
    setCurrentPage(page);
  }, []);
  
  // 🔧 重新設計：定義包含 Sidebar 子分頁的 menuItems
  const menuItems = [
    {
      key: 'settings',
      label: '設定',
      icon: <SettingsIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 直接彈出設定視窗
        handleSettings();
      },
      // 現在兩個設定按鈕功能統一，移除調試標記
      // debug: true,
      // debugColor: '#00FF00',
      // debugLabel: 'MainLayout設定（有API監控）✅'
    },
    {
      key: 'communityPublic',
      label: '社群公開',
      icon: <PublicIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 🔧 修復：在 SPA 模式下保持內部導航，不跳轉路由
        console.log('🎯 BottomBar: SPA 模式導航到社群公開');
        setCurrentPage('manage');
        // 使用內部狀態更新，而不是路由跳轉
        updatePageState('manage', {
          currentSection: 'communityPublic',
          expandedMain: ['home', 'community']
        });
      }
    },
    {
      key: 'teamPoint',
      label: '群組影片',
      icon: <GroupIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 🔧 修復：在 SPA 模式下保持內部導航，不跳轉路由
        console.log('🎯 BottomBar: SPA 模式導航到群組影片');
        setCurrentPage('manage');
        // 使用內部狀態更新，而不是路由跳轉
        updatePageState('manage', {
          currentSection: 'teamPoint',
          expandedMain: ['home', 'teamPoint']
        });
      }
    },
    {
      key: 'expertKnowledge',
      label: '專家筆記',
      icon: <NoteIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 🔧 修復：在 SPA 模式下保持內部導航，不跳轉路由
        console.log('🎯 BottomBar: SPA 模式導航到專家筆記');
        setCurrentPage('manage');
        // 使用內部狀態更新，而不是路由跳轉
        updatePageState('manage', {
          currentSection: 'expertKnowledge',
          expandedMain: ['home', 'community', 'expertKnowledge']
        });
      }
    },
    // 🆕 專案相關功能
    {
      key: 'fileUpload',
      label: '檔案上傳',
      icon: <CloudUploadIcon sx={{ color: '#4CAF50', fontSize: 32 }} />,
      onClick: () => {
        // 🔧 修復：在 SPA 模式下保持內部導航，不跳轉路由
        console.log('🎯 BottomBar: SPA 模式導航到檔案上傳');
        setCurrentPage('manage');
        // 使用內部狀態更新，而不是路由跳轉
        updatePageState('manage', {
          currentSection: 'fileUpload',
          expandedMain: ['home', 'deliveryManagement']
        });
      }
    },
    {
      key: 'projectManagement',
      label: '專案管理',
      icon: <PlaylistPlayIcon sx={{ color: '#F44336', fontSize: 32 }} />,
      onClick: () => {
        // 🔧 修復：在 SPA 模式下保持內部導航，不跳轉路由
        console.log('🎯 BottomBar: SPA 模式導航到專案管理');
        setCurrentPage('manage');
        // 使用內部狀態更新，而不是路由跳轉
        updatePageState('manage', {
          currentSection: 'projectManagement',
          expandedMain: ['home', 'deliveryManagement']
        });
      }
    },
    {
      key: 'clientArea',
      label: '品牌專區',
      icon: <PublicIcon sx={{ color: '#00BCD4', fontSize: 32 }} />,
      onClick: () => {
        // 🔧 修復：在 SPA 模式下保持內部導航，不跳轉路由
        console.log('🎯 BottomBar: SPA 模式導航到品牌專區');
        setCurrentPage('manage');
        // 使用內部狀態更新，而不是路由跳轉
        updatePageState('manage', {
          currentSection: 'clientArea',
          expandedMain: ['home', 'deliveryManagement']
        });
      }
    },
    {
      key: 'clientManagement',
      label: '品牌管理',
      icon: <GroupIcon sx={{ color: '#9C27B0', fontSize: 32 }} />,
      onClick: () => {
        // 🔧 修復：在 SPA 模式下保持內部導航，不跳轉路由
        console.log('🎯 BottomBar: SPA 模式導航到品牌管理');
        setCurrentPage('manage');
        // 使用內部狀態更新，而不是路由跳轉
        updatePageState('manage', {
          currentSection: 'clientManagement',
          expandedMain: ['home', 'deliveryManagement']
        });
      }
    },
    {
      key: 'myVideos',
      label: '我的創作',
      icon: <PhotoLibraryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 🔧 修復：在 SPA 模式下保持內部導航，不跳轉路由
        console.log('🎯 BottomBar: SPA 模式導航到我的創作');
        setCurrentPage('manage');
        // 使用內部狀態更新，而不是路由跳轉
        updatePageState('manage', {
          currentSection: 'myVideos',
          expandedMain: ['home', 'personalCenter', 'myVideos']
        });
      }
    },
    {
      key: 'personalHistory',
      label: '瀏覽記錄',
      icon: <HistoryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        // 🔧 修復：在 SPA 模式下保持內部導航，不跳轉路由
        console.log('🎯 BottomBar: SPA 模式導航到瀏覽記錄');
        setCurrentPage('manage');
        // 使用內部狀態更新，而不是路由跳轉
        updatePageState('manage', {
          currentSection: 'personalHistory',
          expandedMain: ['home', 'personalCenter', 'personalHistory']
        });
      }
    },
    {
      key: 'syncPlay',
      label: '同步播放管理',
      icon: <QrCodeIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        console.log('🎬 MainLayout: 觸發打開同步播放管理器事件');
        // 只觸發事件，由 MainPage 處理
        window.dispatchEvent(new CustomEvent('openSyncManager'));
      }
    },
    {
      key: 'imageManager',
      label: '圖片管理',
      icon: <PhotoLibraryIcon sx={{ color: '#d32f2f', fontSize: 32 }} />,
      onClick: () => {
        console.log('🖼️ MainLayout: 觸發打開圖片管理器事件');
        // 觸發事件，由 MainPage 處理（如果在播放頁）
        window.dispatchEvent(new CustomEvent('openImageManager'));
        // 如果在管理頁，則導航到我的創作
        if (currentPage === 'manage' || currentPage === 'notes') {
          setCurrentPage('manage');
          updatePageState('manage', {
            currentSection: 'myVideos',
            expandedMain: ['home', 'personalCenter', 'myVideos']
          });
        }
      }
    }
  ];
  
  // 當前顯示的頁面
  const [currentPage, setCurrentPage] = useState<PageType>('open');
  
  // 頁面狀態管理
  const [pageStates, setPageStates] = useState<PageState>({
    open: {
      scrollPosition: 0,
      youtubeDialogOpen: false,
      youtubeUrl: '',
      authDialogOpen: false,
      pendingAction: null
    },
    notes: {
      scrollPosition: 0,
      searchKeyword: '',
      selectedMonths: [],
      isSearching: false,
      filteredNotes: []
    },
    manage: {
      currentSection: 'myVideos',
      expandedMain: ['home', 'personalCenter'],
      scrollPosition: 0,
      selectedTab: 0
    },
    main: {
      videoId: null,
      isPlaying: false,
      currentTime: 0,
      playbackRate: 1
    }
  });
  
  // 導航狀態管理
  const [navigationState, setNavigationState] = useState<NavigationState>({
    shouldNavigateToMain: false,
    navigationData: null,
    source: ''
  });

  // 設定視窗狀態管理
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLElement | null>(null);
  // API 監控 Dashboard 狀態
  const [apiMonitorOpen, setApiMonitorOpen] = useState(false);
  const [timecodePosition, setTimecodePosition] = useState<TimecodePosition>(DEFAULT_USER_SETTINGS.timecodePosition);
  const [showTimecode, setShowTimecode] = useState(DEFAULT_USER_SETTINGS.showTimecode); // 🔧 新增：時間碼顯示開關
  const [showTimecodeBackground, setShowTimecodeBackground] = useState(DEFAULT_USER_SETTINGS.showTimecodeBackground); // 🔧 新增：時間碼背景開關
  const [fastForwardSeconds, setFastForwardSeconds] = useState(DEFAULT_USER_SETTINGS.fastForwardSeconds);
  const [stepUnit, setStepUnit] = useState<StepUnit>(DEFAULT_USER_SETTINGS.stepUnit);
  const [playbackDuration, setPlaybackDuration] = useState(DEFAULT_USER_SETTINGS.playbackDuration);
  const [screenshotSize, setScreenshotSize] = useState<ScreenshotSize>(DEFAULT_USER_SETTINGS.screenshotSize);
  const [showWatermarkInOutput, setShowWatermarkInOutput] = useState(DEFAULT_USER_SETTINGS.showWatermarkInOutput);
  const [isSoundEnabled, setIsSoundEnabled] = useState(DEFAULT_USER_SETTINGS.isSoundEnabled);
  const [enableFadeEffect, setEnableFadeEffect] = useState(DEFAULT_USER_SETTINGS.enableFadeEffect);
  
  // 字幕填充行數設定
  const [subtitleFillLines, setSubtitleFillLines] = useState(() => {
    const saved = localStorage.getItem('subtitleFillLines');
    return saved ? parseInt(saved, 10) : DEFAULT_USER_SETTINGS.subtitleFillLines;
  });
  
  // 🆕 用戶設定初始化（從 Firestore 同步）
  useEffect(() => {
    const initSettings = async () => {
      if (authUser?.uid) {
        console.log('☁️ 登入用戶，從 Firestore 載入設定:', authUser.uid);
        
        try {
          const settings = await initializeUserSettings(authUser.uid);
          
          // 更新所有設定狀態
          setTimecodePosition(settings.timecodePosition);
          setShowTimecode(settings.showTimecode); // 🔧 新增
          setShowTimecodeBackground(settings.showTimecodeBackground); // 🔧 新增
          setFastForwardSeconds(settings.fastForwardSeconds);
          setStepUnit(settings.stepUnit);
          setPlaybackDuration(settings.playbackDuration);
          setScreenshotSize(settings.screenshotSize);
          setShowWatermarkInOutput(settings.showWatermarkInOutput);
          setIsSoundEnabled(settings.isSoundEnabled);
          setEnableFadeEffect(settings.enableFadeEffect);
          setSubtitleFillLines(settings.subtitleFillLines);
          
          console.log('✅ 用戶設定已從 Firestore 同步');
        } catch (error) {
          console.error('❌ 從 Firestore 載入設定失敗:', error);
        }
      } else {
        console.log('📂 游客用戶，從 localStorage 載入設定');
        
        // 游客用戶：只使用 localStorage
        const settings = await initializeUserSettings();
        
        setTimecodePosition(settings.timecodePosition);
        setShowTimecode(settings.showTimecode); // 🔧 新增
        setShowTimecodeBackground(settings.showTimecodeBackground); // 🔧 新增
        setFastForwardSeconds(settings.fastForwardSeconds);
        setStepUnit(settings.stepUnit);
        setPlaybackDuration(settings.playbackDuration);
        setScreenshotSize(settings.screenshotSize);
        setShowWatermarkInOutput(settings.showWatermarkInOutput);
        setIsSoundEnabled(settings.isSoundEnabled);
        setEnableFadeEffect(settings.enableFadeEffect);
        setSubtitleFillLines(settings.subtitleFillLines);
      }
    };
    
    initSettings();
  }, [authUser?.uid]); // 當用戶登入狀態變化時重新載入設定
  
  // 更新導航狀態的函數
  const updateNavigationState = useCallback((newState: Partial<NavigationState>) => {
    setNavigationState(prev => ({
      ...prev,
      ...newState
    }));
  }, []);

  // 🔧 關鍵修復：提前定義 updatePageState，避免 TDZ 錯誤
  const updatePageState = useCallback((page: PageType, newState: Partial<PageState[PageType]>) => {
    setPageStates(prev => ({
      ...prev,
      [page]: {
        ...prev[page],
        ...newState
      }
    }));
  }, []);

  // 🔧 重新定義：處理 BottomBar 導航，現在可以安全使用 updateNavigationState 和 updatePageState
  const handleBottomBarNavigationWithState = useCallback((page: PageType, pageStateOverride?: Partial<PageState>) => {
    console.log(`🔄 切換到頁面: ${page}`, pageStateOverride ? `，狀態: ${JSON.stringify(pageStateOverride)}` : '');
    
    // 🔧 性能優化：使用 requestAnimationFrame 避免阻塞 UI
    requestAnimationFrame(() => {
      // 🔧 新增：當切換到 MainPage 時，清除導航狀態（因為這是直接頁面切換，不是新的視頻導航請求）
      if (page === 'main') {
        updateNavigationState({
          shouldNavigateToMain: false,
          navigationData: null,
          source: ''
        });
      }
      
      // 🔧 關鍵修復：如果提供了 pageStateOverride，更新對應頁面的狀態
      if (pageStateOverride && page === 'manage') {
        console.log('🎯 SPA 模式：更新 manage 頁面的 pageState:', pageStateOverride);
        updatePageState('manage', pageStateOverride);
      }
      
      setCurrentPage(page);
    });
  }, [updateNavigationState, updatePageState]);

  // 設定處理函數
  const handleSettings = (event?: React.MouseEvent<HTMLElement>) => {
    if (event && 'currentTarget' in event && event.currentTarget instanceof HTMLElement) {
      setSettingsAnchorEl(event.currentTarget);
    } else {
      // 如果沒有 event 參數，創建一個簡單的錨點元素
      const anchor = document.createElement('div');
      anchor.style.position = 'fixed';
      anchor.style.top = '20px';
      anchor.style.right = '20px';
      anchor.style.width = '1px';
      anchor.style.height = '1px';
      anchor.style.pointerEvents = 'none';
      anchor.style.visibility = 'hidden';
      document.body.appendChild(anchor);
      setSettingsAnchorEl(anchor);
    }
  };

  const handleSettingsClose = () => {
    // 清理錨點元素
    if (settingsAnchorEl && settingsAnchorEl.parentNode) {
      settingsAnchorEl.parentNode.removeChild(settingsAnchorEl);
    }
    setSettingsAnchorEl(null);
  };
  
  // 字幕填充行數變化處理函數
  const handleSubtitleFillLinesChange = (lines: number) => {
    setSubtitleFillLines(lines);
    localStorage.setItem('subtitleFillLines', lines.toString());
  };

  // 🔧 新增：監聽來自 MainPage 的設定事件
  useEffect(() => {
    const handleOpenSettingsEvent = () => {
      console.log('🔧 MainLayout: 收到打開設定事件');
      handleSettings();
    };
    
    window.addEventListener('openSettings', handleOpenSettingsEvent);
    
    return () => {
      window.removeEventListener('openSettings', handleOpenSettingsEvent);
    };
  }, [handleSettings]);

  // 初始化頁面狀態
  useEffect(() => {
    const savedStates = localStorage.getItem('checkplay_page_states');
    if (savedStates) {
      try {
        const parsedStates = JSON.parse(savedStates);
        setPageStates(parsedStates);
        console.log('📱 已恢復頁面狀態:', parsedStates);
      } catch (error) {
        console.error('❌ 恢復頁面狀態失敗:', error);
      }
    }
  }, []);

  // 🔧 修復：保存頁面狀態 - 移除依賴避免無限循環
  const savePageStates = useCallback(() => {
    try {
      localStorage.setItem('checkplay_page_states', JSON.stringify(pageStates));
      console.log('💾 已保存頁面狀態');
    } catch (error) {
      console.error('❌ 保存頁面狀態失敗:', error);
    }
  }, []); // 移除 pageStates 依賴

  // 🔧 修復：定期保存狀態 - 使用 ref 避免依賴循環
  useEffect(() => {
    const interval = setInterval(() => {
      try {
        localStorage.setItem('checkplay_page_states', JSON.stringify(pageStates));
      } catch (error) {
        console.error('❌ 保存頁面狀態失敗:', error);
      }
    }, 10000); // 增加到10秒，減少頻率
    return () => clearInterval(interval);
  }, []); // 移除所有依賴


  // 處理從 NotesPage 到 MainPage 的導航
  const handleNotesToMainNavigation = useCallback((data: any) => {
    // console.log('🎯 從 NotesPage 導航到 MainPage:', data); // 🔧 修復：減少日誌輸出
    setCurrentPage('main');
    
    // 🔧 新增：清除之前的視頻狀態，因為有新的導航請求
    sessionStorage.removeItem('mainPageVideoState');
    
    updateNavigationState({
      shouldNavigateToMain: true,
      navigationData: data,
      source: 'notes'
    });
  }, [updateNavigationState]);

  // 處理從 ManagePages 到 MainPage 的導航
  const handleManageToMainNavigation = useCallback((data: any) => {
    console.log('🎯 從 ManagePages 導航到 MainPage:', data);
    setCurrentPage('main');
    
    // 🔧 新增：清除之前的視頻狀態，因為有新的導航請求
    sessionStorage.removeItem('mainPageVideoState');
    
    updateNavigationState({
      shouldNavigateToMain: true,
      navigationData: data,
      source: 'manage'
    });
  }, [updateNavigationState]);

  // 🔧 極端優化：完全移除消息處理邏輯，避免性能問題
  // useEffect(() => {
  //   // 完全移除消息處理邏輯
  // }, []);

  // 🔧 修復：處理 URL 路由變化 - 只在非 SPA 模式下響應
  useEffect(() => {
    const pathname = location.pathname;
    console.log('🔄 路由變化:', pathname, 'SPA 模式:', pathname === '/app');

    // 🔧 修復：只在非 SPA 模式下響應路由變化
    if (pathname !== '/app') {
      console.log('🔄 傳統路由模式，響應路由變化');
      // 根據路由設置當前頁面
      if (pathname.startsWith('/notes')) {
        setCurrentPage('notes');
      } else if (pathname.startsWith('/main') || pathname.startsWith('/v/')) {
        setCurrentPage('main');
      } else if (pathname.startsWith('/manage') || pathname.startsWith('/favorite')) {
        setCurrentPage('manage');
      } else if (pathname.startsWith('/open') || pathname === '/') {
        setCurrentPage('open');
      }
    } else {
      console.log('🔄 SPA 模式，忽略路由變化');
    }
  }, [location.pathname]);

  // 🔧 修復：渲染所有頁面組件但只顯示當前頁面，避免重新掛載
  const renderAllPages = () => {
    return (
      <>
        {/* Open 頁面 */}
        <Box sx={{ 
          display: currentPage === 'open' ? 'block' : 'none',
          height: '100%',
          width: '100%',
          overflow: 'auto' // 🔧 修復：添加滾動支持
        }}>
          <OpenPage 
            onPageChange={handleBottomBarNavigationWithState}
            isEmbedded={true}
          />
        </Box>

        {/* Notes 頁面 */}
        <Box sx={{ 
          display: currentPage === 'notes' ? 'block' : 'none',
          height: '100%',
          width: '100%',
          overflow: 'auto' // 🔧 修復：添加滾動支持
        }}>
          <NotesPage
            pageState={pageStates.notes}
            onStateChange={(newState) => updatePageState('notes', newState)}
            onNavigateToMain={handleNotesToMainNavigation}
            isEmbedded={true}
          />
        </Box>

        {/* Manage 頁面 */}
        <Box sx={{ 
          display: currentPage === 'manage' ? 'block' : 'none',
          height: '100%',
          width: '100%',
          overflow: 'auto' // 🔧 修復：添加滾動支持
        }}>
          <ManagePages
            pageState={pageStates.manage}
            onStateChange={(newState) => updatePageState('manage', newState)}
            onNavigateToMain={handleManageToMainNavigation}
            onPageChange={handleBottomBarNavigationWithState}
            isEmbedded={true}
          />
        </Box>

        {/* Main 頁面 */}
        <Box sx={{ 
          display: currentPage === 'main' ? 'block' : 'none',
          height: '100%',
          width: '100%',
          overflow: 'auto' // 🔧 修復：允許 MainPage 滾動
        }}>
          <MainPage
            navigationState={navigationState}
            pageState={pageStates.main}
            onStateChange={(newState) => updatePageState('main', newState)}
            onNavigateBack={handleBottomBarNavigationWithState}
            isEmbedded={true}
            subtitleFillLines={subtitleFillLines}
          />
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100vh',
      width: '100vw',
      overflow: 'hidden'
    }}>
      {/* 主內容區域 */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden',
        position: 'relative'
      }}>
        {renderAllPages()}
      </Box>
      
      {/* 🔧 重構：統一 BottomBar - 在所有頁面顯示（除了首頁） */}
      {/* 根據當前頁面動態過濾菜單項 */}
      {currentPage !== 'open' && (() => {
        // 🎯 播放頁（MainPage）：只顯示 4 個項目
        const mainPageKeys = ['settings', 'imageManager', 'fileUpload', 'projectManagement'];
        
        // 根據當前頁面過濾菜單項
        const filteredMenuItems = currentPage === 'main' 
          ? menuItems.filter(item => mainPageKeys.includes(item.key))
          : menuItems; // 筆記頁、管理頁：顯示所有項目
        
        return (
          <BottomBar
            menuItems={filteredMenuItems}
            currentPage={currentPage}
            onPageChange={handleBottomBarNavigationWithState}
            isEmbedded={true}
          />
        );
      })()}

      {/* 設定視窗 */}
      <SettingsMenu
        anchorEl={settingsAnchorEl}
        open={Boolean(settingsAnchorEl)}
        onClose={handleSettingsClose}
        timecodePosition={timecodePosition}
        setTimecodePosition={setTimecodePosition}
        showTimecode={showTimecode} // 🔧 新增
        setShowTimecode={setShowTimecode} // 🔧 新增
        showTimecodeBackground={showTimecodeBackground} // 🔧 新增
        setShowTimecodeBackground={setShowTimecodeBackground} // 🔧 新增
        fastForwardSeconds={fastForwardSeconds}
        setFastForwardSeconds={setFastForwardSeconds}
        stepUnit={stepUnit}
        setStepUnit={setStepUnit}
        playbackDuration={playbackDuration}
        setPlaybackDuration={setPlaybackDuration}
        screenshotSize={screenshotSize}
        setScreenshotSize={setScreenshotSize}
        showWatermarkInOutput={showWatermarkInOutput}
        setShowWatermarkInOutput={setShowWatermarkInOutput}
        isSoundEnabled={isSoundEnabled}
        setIsSoundEnabled={setIsSoundEnabled}
        enableFadeEffect={enableFadeEffect}
        setEnableFadeEffect={setEnableFadeEffect}
        subtitleFillLines={subtitleFillLines}
        setSubtitleFillLines={handleSubtitleFillLinesChange}
        onApiMonitorOpen={() => setApiMonitorOpen(true)}
      />

      {/* API 監控 Dashboard */}
      {apiMonitorOpen && (
        <Suspense fallback={<div>載入中...</div>}>
          <ApiMonitorDialog
            open={apiMonitorOpen}
            onClose={() => setApiMonitorOpen(false)}
          />
        </Suspense>
      )}

      {/* 同步播放管理器 - MainLayout 不需要渲染，由 MainPage 處理 */}
      {/* SyncPlayManager 已移到 MainPage 中處理 */}
    </Box>
  );
};

export default MainLayout;
